<template>
  <div
    class="credit-monthly-view i-mt-13 i-radius-xl"
    data-testid="credit-monthly-view"
  >
    <div
      class="credit-monthly-view__header d-flex justify-content-between i-p-10"
    >
      <h3 class="i-text-neutral-120 i-text-subheading-1">
        Month by Month Insights View
      </h3>
      <div v-if="!isLoading" class="d-flex align-items-center">
        <i-icon-button
          type="transparent"
          size="sm"
          :symbol-stroke="1.5"
          symbol="small-arrows-left-small"
          class="i-mr-1"
          @button-click="scrollLeft"
        />
        <div class="credit-monthly-view__dots d-flex align-items-center">
          <span
            v-for="(dot, index) in fields"
            :key="index"
            class="dot"
            :class="{ visible: isColumnVisible(index) }"
          ></span>
        </div>

        <i-icon-button
          type="transparent"
          size="sm"
          :symbol-stroke="1.5"
          symbol="small-arrows-right-small"
          class="i-ml-1"
          @button-click="scrollRight"
        />
      </div>
    </div>
    <div ref="creditMonthlyTable" class="credit-monthly-view__table">
      <i-spinner
        v-if="isLoading"
        size="lg"
        type="neutral"
        subtype="subtle"
        class="mx-auto i-my-16"
        data-testid="loading-credit-monthly-table"
      />
      <b-table
        v-else
        class="i-table"
        :sticky-header="stickyHeader"
        no-border-collapse
        responsive
        :items="items"
        :fields="fields"
        data-testid="credit-insights-monthly-table"
      >
        <template #head()="data">
          <div class="text-nowrap">
            {{ data.label }}
          </div>
        </template>
        <template #head(monthly_count_actual_deposits)="data">
          <div class="d-flex flex-row align-items-center">
            <span class="i-mr-xs text-nowrap">{{ data.label }}</span>
            <info-tooltip
              icon-class="i-text-neutral-90"
              title="Actual Deposits Count"
              subtitle="The count of all income transactions"
            ></info-tooltip>
          </div>
        </template>
        <template #head(monthly_count_gross_deposits)="data">
          <div class="d-flex flex-row align-items-center">
            <span class="i-mr-xs text-nowrap">{{ data.label }}</span>
            <info-tooltip
              icon-class="i-text-neutral-90"
              title="Gross Deposits Count"
              subtitle="The count of all incoming transactions"
            ></info-tooltip>
          </div>
        </template>
        <!-- specifically format year_month -->
        <template #head(year_month)="data">
          <div class="text-nowrap i-mr-6">
            {{ data.label }}
          </div>
        </template>
        <template #cell(year_month)="data">
          <div class="text-center">
            {{
              data.value ? $moment(`${data.value}-01`).format('MMM YYYY') : '-'
            }}
          </div>
        </template>
        <!-- default cell format -->
        <template #cell()="data">
          <div class="text-center text-nowrap">
            {{ formatDefault(data.value) }}
          </div>
        </template>
        <!-- specific columns formatted as currencies -->
        <template #head(monthly_starting_balance)="data">
          <div class="d-flex flex-row align-items-center">
            <span class="i-mr-xs text-nowrap">{{ data.label }}</span>
            <info-tooltip
              icon-class="i-text-neutral-90"
              title="Beginning Balance"
              subtitle="The earliest balance available in the data for the given month"
            ></info-tooltip>
          </div>
        </template>
        <template #cell(monthly_starting_balance)="data">
          <div class="text-center">{{ formatAsCurrency(data.value) }}</div>
        </template>
        <template #head(monthly_ending_balance)="data">
          <div class="d-flex flex-row align-items-center">
            <span class="i-mr-xs text-nowrap">{{ data.label }}</span>
            <info-tooltip
              icon-class="i-text-neutral-90"
              title="Ending Balance"
              subtitle="The latest balance available in the data for the given month"
            ></info-tooltip>
          </div>
        </template>
        <template #cell(monthly_ending_balance)="data">
          <div class="text-center">{{ formatAsCurrency(data.value) }}</div>
        </template>
        <template #head(monthly_gross_deposit_amount)="data">
          <div class="d-flex flex-row align-items-center">
            <span class="i-mr-xs text-nowrap">{{ data.label }}</span>
            <info-tooltip
              icon-class="i-text-neutral-90"
              title="Gross Deposit Amount"
              subtitle="The sum of all incoming transaction amounts"
            ></info-tooltip>
          </div>
        </template>
        <template #cell(monthly_gross_deposit_amount)="data">
          <div class="text-center">{{ formatAsCurrency(data.value) }}</div>
        </template>
        <template #head(monthly_actual_deposit_amount)="data">
          <div class="d-flex flex-row align-items-center">
            <span class="i-mr-xs text-nowrap">{{ data.label }}</span>
            <info-tooltip
              icon-class="i-text-neutral-90"
              title="Actual Deposit Amount"
              subtitle="The sum of all income transaction amounts"
            ></info-tooltip>
          </div>
        </template>
        <template #cell(monthly_actual_deposit_amount)="data">
          <div class="text-center">{{ formatAsCurrency(data.value) }}</div>
        </template>
        <template #cell(monthly_average_daily_balance)="data">
          <div class="text-center">{{ formatAsCurrency(data.value) }}</div>
        </template>
        <template #head(monthly_debit_amount)="data">
          <div class="d-flex flex-row align-items-center">
            <span class="i-mr-xs text-nowrap">{{ data.label }}</span>
            <info-tooltip
              icon-class="i-text-neutral-90"
              title="Pulls"
              subtitle="Total amount debited out of the account(s)"
            ></info-tooltip>
          </div>
        </template>
        <template #cell(monthly_debit_amount)="data">
          <div class="text-center">{{ formatAsCurrency(data.value) }}</div>
        </template>
        <template #head(monthly_loan_total_disbursements)="data">
          <div class="d-flex flex-row align-items-center">
            <span class="i-mr-xs text-nowrap">{{ data.label }}</span>
            <info-tooltip
              icon-class="i-text-neutral-90"
              title="Fundings"
              subtitle="The total incoming loan amounts for the given month"
            ></info-tooltip>
          </div>
        </template>
        <template #cell(monthly_loan_total_disbursements)="data">
          <div class="text-center">{{ formatAsCurrency(data.value) }}</div>
        </template>
        <template #cell(monthly_loan_total_repayments)="data">
          <div class="text-center">{{ formatAsCurrency(data.value) }}</div>
        </template>
        <template #head(monthly_transfers_in)="data">
          <div class="d-flex flex-row align-items-center">
            <span class="i-mr-xs text-nowrap">{{ data.label }}</span>
            <info-tooltip
              icon-class="i-text-neutral-90"
              title="Bank Transfers In"
              subtitle="Total bank transfers incoming"
            ></info-tooltip>
          </div>
        </template>
        <template #cell(monthly_transfers_in)="data">
          <div class="text-center">{{ formatAsCurrency(data.value) }}</div>
        </template>
        <template #head(monthly_transfers_out)="data">
          <div class="d-flex flex-row align-items-center">
            <span class="i-mr-xs text-nowrap">{{ data.label }}</span>
            <info-tooltip
              icon-class="i-text-neutral-90"
              title="Bank Transfers Out"
              subtitle="Total bank transfers outgoing"
            ></info-tooltip>
          </div>
        </template>
        <template #cell(monthly_transfers_out)="data">
          <div class="text-center">{{ formatAsCurrency(data.value) }}</div>
        </template>
        <template #head(loans)="data">
          <div class="d-flex flex-row align-items-center">
            <span class="i-mr-xs text-nowrap">{{ data.label }}</span>
            <info-tooltip
              icon-class="i-text-neutral-90"
              title="Loans"
              subtitle="Information about each loan found in the transaction data"
            ></info-tooltip>
          </div>
        </template>
        <template #cell(loans)="data">
          <div style="white-space: nowrap">
            <ol v-for="item in data.value" :key="item">
              {{
                item
              }}
            </ol>
          </div>
        </template>
      </b-table>
    </div>
    <div
      v-if="isError"
      class="d-flex flex-column align-items-center i-p-12"
      data-testid="credit-monthly-table-error"
    >
      <i-alert-message
        class="credit-monthly-view__error-message"
        type="error"
        heading="Error loading data"
        size="md"
      />
    </div>
  </div>
</template>

<script>
import { iAlertMessage, iSpinner, iIconButton } from '@inscribe/signal';
import infoTooltip from '@/shared/components/InfoTooltip';
import { toCurrency } from '@/utils/toCurrency';
import { mapState } from 'vuex';
import { isErrorResponse } from '@/V2/stores/CreditStore';

const TABLE_SELECTOR = '.credit-monthly-view__table';

const FALLBACK_ITEM = {
  year_month: null,
  bank_name: null,
  bank_account_last_4: null,
  monthly_starting_balance: null,
  monthly_ending_balance: null,
  monthly_actual_deposit_amount: null,
  monthly_count_actual_deposits: null,
  monthly_debit_amount: null,
  monthly_gross_deposit_amount: null,
  monthly_count_gross_deposits: null,
  monthly_average_daily_balance: null,
  monthly_count_negative_balance_days: null,
  monthly_loan_total_disbursements: null,
  monthly_loan_total_repayments: null,
  monthly_transfers_in: null,
  monthly_transfers_out: null,
  loans: null,
};

export default {
  name: 'CreditMonthlyTableView',
  components: {
    iAlertMessage,
    iSpinner,
    iIconButton,
    infoTooltip,
  },
  data() {
    return {
      toCurrency,
      stickyHeader: true,
      visilibleColumns: [],
      fields: [
        {
          key: 'year_month',
          label: 'Current Month',
          stickyColumn: true,
          thClass: 'sticky-column-header',
          tdClass: 'sticky-column-cell i-text-subheading-2',
          sortable: true,
        },
        { key: 'bank_name', label: 'Bank Name' },
        { key: 'bank_account_last_4', label: 'Account Number (last 4 digits)' },
        { key: 'monthly_starting_balance', label: 'Starting Balance' },
        { key: 'monthly_ending_balance', label: 'Ending Balance' },
        {
          key: 'monthly_actual_deposit_amount',
          label: 'Total Income Deposits',
        },
        {
          key: 'monthly_count_actual_deposits',
          label: 'Income Deposits Count',
        },
        { key: 'monthly_debit_amount', label: 'Total Debits' },
        { key: 'monthly_gross_deposit_amount', label: 'Total Credits' },
        {
          key: 'monthly_count_gross_deposits',
          label: 'Credit Transactions Count',
        },
        { key: 'monthly_average_daily_balance', label: 'Avg. Daily Balance' },
        {
          key: 'monthly_count_negative_balance_days',
          label: 'Negative Balance Days',
        },
        {
          key: 'monthly_loan_total_disbursements',
          label: 'Loan Disbursements',
        },
        { key: 'monthly_loan_total_repayments', label: 'Loan Repayments' },
        { key: 'monthly_transfers_in', label: 'Transfers In' },
        { key: 'monthly_transfers_out', label: 'Transfers Out' },
        { key: 'loans', label: 'Loans' },
      ],
    };
  },
  computed: {
    ...mapState({
      credit: 'credit',
    }),
    creditInsightsByMonthData() {
      return this.credit?.creditInsightsByMonth;
    },
    isLoading() {
      return this.creditInsightsByMonthData === undefined;
    },
    isError() {
      if (this.isLoading === false) {
        return isErrorResponse(this.creditInsightsByMonthData);
      }
      return false;
    },
    items() {
      if (this.isLoading) {
        return [FALLBACK_ITEM];
      } else {
        return this.creditInsightsByMonthData;
      }
    },
  },
  watch: {
    items() {
      this.$nextTick(() => {
        this.manageScrollEvent('remove');
        this.manageScrollEvent('add');
        this.getVisibleCells();
      });
    },
  },
  destroyed() {
    this.manageScrollEvent('remove');
  },
  methods: {
    formatAsCurrency(value) {
      return value === null ? '-' : `${toCurrency(value, 2)}`;
    },
    formatDefault(value) {
      return value === null || value === '' ? '-' : value;
    },
    scrollLeft() {
      const tableElement = document.querySelector(TABLE_SELECTOR);
      tableElement.scrollLeft -= 100; // adjust the value as needed
      this.getVisibleCells();
    },
    scrollRight() {
      const tableElement = document.querySelector(TABLE_SELECTOR);
      tableElement.scrollLeft += 100; //
      this.getVisibleCells();
    },
    getVisibleCells() {
      const containerElement = document.querySelector(TABLE_SELECTOR);
      if (!containerElement) {
        return;
      }
      const tableElement = document.querySelector('.i-table table thead');
      const firstRow = tableElement.querySelector('tr');
      const cells = firstRow.querySelectorAll('th');
      const containerRect = containerElement.getBoundingClientRect();
      this.visilibleColumns = [];

      cells.forEach((cell, index) => {
        const cellRect = cell.getBoundingClientRect();
        if (
          cellRect.right <= containerRect.right &&
          cellRect.left >= containerRect.left
        ) {
          this.visilibleColumns.push(index);
        }
      });
    },
    manageScrollEvent(action) {
      const table = document.querySelector(TABLE_SELECTOR);
      if (action === 'add') {
        table && table.addEventListener('scroll', this.getVisibleCells);
      } else if (action === 'remove') {
        table && table.removeEventListener('scroll', this.getVisibleCells);
      }
    },
    isColumnVisible(index) {
      return this.visilibleColumns.includes(index);
    },
  },
};
</script>

<style lang="scss" scoped>
.credit-monthly-view {
  overflow: hidden;
  border: 1px solid $light-neutral-60;
  &__dots {
    .dot {
      display: inline-block;
      width: 5px;
      height: 5px;
      border-radius: 50%;
      background: $light-neutral-80;
      margin: 0 2px;
      &.visible {
        background: $light-neutral-110;
      }
    }
  }
  &__table {
    overflow: scroll;
    max-height: 300px;
    ::v-deep .i-table {
      max-height: 298px;
      margin-bottom: 0 !important;
      table {
        thead {
          tr {
            th {
              color: $light-neutral-90 !important;
              border-bottom: 1px solid $light-neutral-50 !important;
              &.sticky-column-header {
                padding: map-get($increments, 6) !important;
                position: sticky !important;
                left: 0 !important;
                top: 0 !important;
                z-index: 3 !important;
              }
            }
          }
        }
        tbody {
          tr {
            td {
              &.sticky-column-cell {
                padding-left: map-get($increments, 6) !important;
                background: $light-neutral-20 !important;
                color: $light-neutral-120 !important;
                border-right: 1px solid $light-neutral-50 !important;
              }
              color: $light-neutral-120 !important;
            }
          }
        }
      }
    }
  }
  &__error-message {
    width: 300px;
  }
}
</style>
